import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Form, Icon } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, useSelectedDealer } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { DnsRecord } from "modules/DealerOverviewDashboard/components/DmsSystem/components/Network/components";
import {
  useCreateDomainMutation,
  useDeleteDomainMutation,
  useVerifyDomainInfoMutation
} from "modules/DealerOverviewDashboard/components/DmsSystem/components/Network/hooks";
import "modules/DealerOverviewDashboard/components/DmsSystem/components/Network/Network.scss";

import type { Domain } from "models";
import type { SendingDnsRecord } from "modules/DealerOverviewDashboard/components/DmsSystem/components/Network/components";
import type { DealerOverviewFormPayload } from "modules/DealerOverviewDashboard/hooks";

const FormHelper = createFormHelper<DealerOverviewFormPayload>();

//TODO: The generated Domain model types `sending_dns_records` as string[], but the backend
// actually returns DNS record objects. The cast is unavoidable until the model is fixed —
const getSendingDnsRecords = (domain?: Domain) => (domain?.sending_dns_records ?? []) as unknown as SendingDnsRecord[];

export const Network = () => {
  const { t } = useTranslation();
  const { watch, resetField } = FormHelper.useFormContext();

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { selectedDealer } = useSelectedDealer();
  const { mutate: createDomain, isPending: isCreateDomainPending } = useCreateDomainMutation();
  const { mutate: verifyDomain, isPending: isVerifyDomainPending } = useVerifyDomainInfoMutation();
  const { mutate: deleteDomain, isPending: isDeleteDomainPending } = useDeleteDomainMutation();

  const dealerDomainName = selectedDealer?.domain?.name ?? selectedDealer?.domain_name ?? "";

  useEffect(() => {
    resetField("domain_name", { defaultValue: dealerDomainName });
  }, [dealerDomainName]);

  if (!selectedDealer || !selectedDealer.custom_domain_enabled) return null;
  const domain = selectedDealer.domain;

  const isDomainVerified = domain?.state === "active";
  const dnsRecords = getSendingDnsRecords(domain).filter(({ record_type }) => record_type === "TXT");
  const domainName = watch("domain_name");
  const domainPayload = { name: domainName, dealer_id: selectedDealer.id };

  const handleDelete = () => {
    deleteDomain(selectedDealer.id);
    setIsDeleteConfirmOpen(false);
  };

  return (
    <>
      <div className="Network">
        <div className="dealer-overview-subheading">{t("v8_network", "Network")}</div>
        <Form.Group className="flex-start">
          <Form.Field width={8}>
            <label>{t("v8_domain_name", "Domain name")}</label>
            <div className="domain-container">
              <FormHelper.Field.Input
                name="domain_name"
                inputProps={{ label: `${t("v8_no_reply", "No-reply")}@`, labelPosition: "left", placeholder: t("v8_domain_name", "Domain name") }}
              />

              <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.DealerDomains}>
                <div className="align-self-end">
                  <Button
                    color="green"
                    type="button"
                    onClick={() => createDomain(domainPayload)}
                    disabled={isCreateDomainPending || !domainName}
                    loading={isCreateDomainPending}
                  >
                    {t("v8_update_domain", "Update Domain")}
                  </Button>
                </div>
              </Can>
            </div>
          </Form.Field>
          {selectedDealer.domain && selectedDealer.domain.sending_dns_records ? (
            <Form.Field width={8}>
              <div className="domain-container">
                {dnsRecords.map(record => (
                  <DnsRecord t={t} record={record} isDomainVerified={isDomainVerified} key={`${record.Name}-${record.Value}`} />
                ))}

                {domainName && (
                  <div className="dns-record-status">
                    <span>{isDomainVerified ? t("v8_domain_is_verified", "Domain is verified") : t("v8_domain_not_verified", "Domain is not verified")}</span>
                    <div>
                      <Icon className={isDomainVerified ? "check circle" : "circle xmark"} color={isDomainVerified ? "green" : "red"} />
                    </div>
                  </div>
                )}
                <div className="dns-record-actions">
                  <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.DealerDomains}>
                    <Button
                      type="button"
                      color="orange"
                      disabled={isDomainVerified || isVerifyDomainPending || !domainName}
                      loading={isVerifyDomainPending}
                      onClick={() => verifyDomain(domainPayload)}
                    >
                      {t("v8_verify_domain", "Verify Domain")}
                      <Icon className="rotate" />
                    </Button>
                  </Can>
                  <div className="buttons-container">
                    <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.DealerDomains}>
                      <Button color="red" type="button" onClick={() => setIsDeleteConfirmOpen(true)} disabled={isDeleteDomainPending || !domainName}>
                        {t("v8_delete", "Delete")}
                        <Icon className="trash" />
                      </Button>
                    </Can>
                  </div>
                </div>
              </div>
            </Form.Field>
          ) : null}
        </Form.Group>
      </div>
      <Divider />

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={isDeleteConfirmOpen}
        handleCancel={() => setIsDeleteConfirmOpen(false)}
        customTitle={t("v8_are_you_sure", "Are you sure?")}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleDelete}
        isLoading={isDeleteDomainPending}
      />
    </>
  );
};
